<template>
    <div v-if="show" class="modal-overlay" @click.self="closeModal">
        <div class="modal-content">
            <!-- Header -->
            <div class="modal-header">
                <h2 class="modal-title">✨ Refine Story</h2>
                <button class="close-button" @click="closeModal" aria-label="Close">
                    ×
                </button>
            </div>

            <!-- Body -->
            <div class="modal-body">
                <!-- Instructions Section -->
                <div v-if="!results" class="instructions-section">
                    <label for="refinement-instructions" class="input-label">
                        What would you like to improve?
                    </label>
                    <textarea
                        id="refinement-instructions"
                        v-model="instructions"
                        placeholder="E.g., 'Make it more engaging and fix any grammar issues' or 'Simplify the language and improve flow'"
                        rows="4"
                        class="instructions-input"
                        :disabled="loading"
                    ></textarea>

                    <!-- Example Suggestions -->
                    <div class="suggestions">
                        <span class="suggestion-label">Quick suggestions:</span>
                        <button
                            v-for="suggestion in exampleSuggestions"
                            :key="suggestion"
                            @click="instructions = suggestion"
                            class="suggestion-chip"
                            :disabled="loading"
                        >
                            {{ suggestion }}
                        </button>
                    </div>

                    <!-- Options Checkboxes -->
                    <div class="options-section">
                        <label class="checkbox-label">
                            <input
                                type="checkbox"
                                v-model="options.refine_content"
                                :disabled="loading"
                            />
                            <span>Refine full content</span>
                        </label>
                        <label class="checkbox-label">
                            <input
                                type="checkbox"
                                v-model="options.improve_title"
                                :disabled="loading"
                            />
                            <span>Improve title</span>
                        </label>
                        <label class="checkbox-label">
                            <input
                                type="checkbox"
                                v-model="options.refine_excerpt"
                                :disabled="loading"
                            />
                            <span>Generate excerpt</span>
                        </label>
                        <label class="checkbox-label">
                            <input
                                type="checkbox"
                                v-model="options.refine_tags"
                                :disabled="loading"
                            />
                            <span>Refine tags</span>
                        </label>
                    </div>

                    <!-- Error Display -->
                    <div v-if="error" class="error-message">
                        {{ error }}
                    </div>

                    <!-- Action Buttons -->
                    <div class="action-buttons">
                        <button
                            @click="closeModal"
                            class="secondary-button"
                            :disabled="loading"
                        >
                            Cancel
                        </button>
                        <button
                            @click="generateRefinement"
                            class="primary-button"
                            :disabled="loading || !isValid"
                        >
                            <span v-if="loading" class="loading-spinner"></span>
                            <span v-else>✨ Refine Content</span>
                        </button>
                    </div>
                </div>

                <!-- Results Section -->
                <div v-else class="results-section">
                    <!-- Changes Summary -->
                    <div class="changes-summary">
                        <h3 class="section-title">📝 Changes Summary</h3>
                        <p class="summary-text">{{ results.changes_summary }}</p>
                    </div>

                    <!-- Improved Title -->
                    <div v-if="results.improved_title" class="result-item">
                        <div class="result-header">
                            <h3 class="section-title">📌 Improved Title</h3>
                            <button
                                @click="applyTitle"
                                class="apply-button"
                                :disabled="appliedTitle"
                            >
                                {{ appliedTitle ? '✓ Applied' : 'Apply' }}
                            </button>
                        </div>
                        <div class="result-content">
                            <input
                                v-model="editedTitle"
                                type="text"
                                class="editable-title"
                                placeholder="Edit the title..."
                            />
                        </div>
                    </div>

                    <!-- Generated Excerpt -->
                    <div v-if="results.refined_excerpt" class="result-item">
                        <div class="result-header">
                            <h3 class="section-title">📝 Generated Excerpt</h3>
                            <button
                                @click="applyExcerpt"
                                class="apply-button"
                                :disabled="appliedExcerpt"
                            >
                                {{ appliedExcerpt ? '✓ Applied' : 'Apply' }}
                            </button>
                        </div>
                        <div class="result-content">
                            <textarea
                                v-model="editedExcerpt"
                                class="editable-excerpt"
                                placeholder="Edit the excerpt..."
                                rows="3"
                            ></textarea>
                        </div>
                    </div>

                    <!-- Refined Content -->
                    <div v-if="results.refined_content" class="result-item">
                        <div class="result-header">
                            <h3 class="section-title">📄 Refined Content</h3>
                            <button
                                @click="applyContent"
                                class="apply-button"
                                :disabled="appliedContent"
                            >
                                {{ appliedContent ? '✓ Applied' : 'Apply' }}
                            </button>
                        </div>
                        <div class="result-content">
                            <textarea
                                v-model="editedContent"
                                class="editable-content"
                                placeholder="Edit the content..."
                                rows="12"
                            ></textarea>
                        </div>
                    </div>

                    <!-- Refined Tags -->
                    <div v-if="results.refined_tags && results.refined_tags.length > 0" class="result-item">
                        <div class="result-header">
                            <h3 class="section-title">🏷️ Refined Tags</h3>
                            <button
                                @click="applyTags"
                                class="apply-button"
                                :disabled="appliedTags"
                            >
                                {{ appliedTags ? '✓ Applied' : 'Apply' }}
                            </button>
                        </div>
                        <div class="result-content">
                            <div class="tags-preview">
                                <span
                                    v-for="(tag, index) in editedTags"
                                    :key="index"
                                    class="tag-chip"
                                >
                                    {{ tag }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Metadata -->
                    <div class="metadata-section">
                        <div class="metadata-item">
                            <span class="metadata-label">Model:</span>
                            <span class="metadata-value">{{ results.metadata.model }}</span>
                        </div>
                        <div class="metadata-item">
                            <span class="metadata-label">Tokens:</span>
                            <span class="metadata-value">{{ results.metadata.tokens_used }}</span>
                        </div>
                        <div class="metadata-item">
                            <span class="metadata-label">Cost:</span>
                            <span class="metadata-value">${{ results.metadata.estimated_cost_usd.toFixed(4) }}</span>
                        </div>
                        <div class="metadata-item">
                            <span class="metadata-label">Time:</span>
                            <span class="metadata-value">{{ results.metadata.processing_time_ms }}ms</span>
                        </div>
                        <div v-if="results.metadata.cached" class="metadata-item cached">
                            <span class="metadata-label">✓ Cached</span>
                        </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="action-buttons">
                        <button
                            @click="refineAgain"
                            class="secondary-button"
                        >
                            🔄 Refine Again
                        </button>
                        <button
                            @click="applyAll"
                            class="primary-button"
                            :disabled="allApplied"
                        >
                            {{ allApplied ? '✓ All Applied' : '✨ Apply All' }}
                        </button>
                        <button
                            @click="closeModal"
                            class="secondary-button"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useRefinement } from '@/services/refinement';

export default {
    name: 'RefinementModal',
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        postId: {
            type: String,
            required: true,
        },
    },
    emits: ['close', 'apply-title', 'apply-excerpt', 'apply-content', 'apply-tags'],
    setup(props, { emit }) {
        const { refineContent } = useRefinement();

        // State
        const instructions = ref('');
        const options = ref({
            refine_content: true,
            improve_title: true,
            refine_excerpt: true,
            refine_tags: true,
        });
        const loading = ref(false);
        const error = ref('');
        const results = ref(null);
        const appliedTitle = ref(false);
        const appliedExcerpt = ref(false);
        const appliedContent = ref(false);
        const appliedTags = ref(false);

        // Editable versions of refinement results
        const editedTitle = ref('');
        const editedExcerpt = ref('');
        const editedContent = ref('');
        const editedTags = ref([]);

        // Example suggestions
        const exampleSuggestions = [
            'Make it more engaging and fix grammar',
            'Simplify language and improve flow',
            'Add more details and examples',
            'Make it more concise and clear',
        ];

        // Computed
        const isValid = computed(() => {
            if (!instructions.value.trim() || instructions.value.trim().length < 5) {
                return false;
            }
            if (!options.value.refine_content && !options.value.improve_title && !options.value.refine_excerpt && !options.value.refine_tags) {
                return false;
            }
            return true;
        });

        const allApplied = computed(() => {
            let allChecked = true;
            if (results.value?.improved_title && !appliedTitle.value) allChecked = false;
            if (results.value?.refined_excerpt && !appliedExcerpt.value) allChecked = false;
            if (results.value?.refined_content && !appliedContent.value) allChecked = false;
            if (results.value?.refined_tags && results.value.refined_tags.length > 0 && !appliedTags.value) allChecked = false;
            return allChecked;
        });

        // Methods
        const closeModal = () => {
            emit('close');
        };

        const generateRefinement = async () => {
            if (!isValid.value) return;

            loading.value = true;
            error.value = '';

            try {
                const result = await refineContent(
                    props.postId,
                    instructions.value,
                    options.value
                );
                results.value = result;
                appliedTitle.value = false;
                appliedExcerpt.value = false;
                appliedContent.value = false;
                appliedTags.value = false;

                // Initialize editable versions with AI suggestions
                editedTitle.value = result.improved_title || '';
                editedExcerpt.value = result.refined_excerpt || '';
                editedContent.value = result.refined_content || '';
                editedTags.value = result.refined_tags || [];
            } catch (err) {
                error.value = err.message || 'Failed to refine content. Please try again.';
                console.error('Refinement error:', err);
            } finally {
                loading.value = false;
            }
        };

        const applyTitle = () => {
            if (editedTitle.value) {
                emit('apply-title', editedTitle.value);
                appliedTitle.value = true;
            }
        };

        const applyExcerpt = () => {
            if (editedExcerpt.value) {
                emit('apply-excerpt', editedExcerpt.value);
                appliedExcerpt.value = true;
            }
        };

        const applyContent = () => {
            if (editedContent.value) {
                emit('apply-content', editedContent.value);
                appliedContent.value = true;
            }
        };

        const applyTags = () => {
            if (editedTags.value && editedTags.value.length > 0) {
                emit('apply-tags', editedTags.value);
                appliedTags.value = true;
            }
        };

        const applyAll = () => {
            applyTitle();
            applyExcerpt();
            applyContent();
            applyTags();
        };

        const refineAgain = () => {
            results.value = null;
            error.value = '';
        };

        // Reset state when modal opens/closes
        watch(() => props.show, (newShow) => {
            if (newShow) {
                // Reset when opening
                instructions.value = '';
                options.value = {
                    refine_content: true,
                    improve_title: true,
                    refine_excerpt: true,
                    refine_tags: true,
                };
                error.value = '';
                results.value = null;
                appliedTitle.value = false;
                appliedExcerpt.value = false;
                appliedContent.value = false;
                appliedTags.value = false;
            }
        });

        return {
            instructions,
            options,
            loading,
            error,
            results,
            appliedTitle,
            appliedExcerpt,
            appliedContent,
            appliedTags,
            editedTitle,
            editedExcerpt,
            editedContent,
            editedTags,
            exampleSuggestions,
            isValid,
            allApplied,
            closeModal,
            generateRefinement,
            applyTitle,
            applyExcerpt,
            applyContent,
            applyTags,
            applyAll,
            refineAgain,
        };
    },
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
}

.modal-content {
    background: white;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    max-width: 800px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px;
    border-bottom: 1px solid #e5e7eb;
}

.modal-title {
    font-size: 24px;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
}

.close-button {
    background: none;
    border: none;
    font-size: 32px;
    color: #6b7280;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.2s;
}

.close-button:hover {
    background-color: #f3f4f6;
}

.modal-body {
    padding: 24px;
}

/* Instructions Section */
.instructions-section {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.input-label {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: -12px;
}

.instructions-input {
    width: 100%;
    padding: 12px;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    resize: vertical;
    transition: border-color 0.2s;
}

.instructions-input:focus {
    outline: none;
    border-color: #667eea;
}

.instructions-input:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
}

/* Suggestions */
.suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
}

.suggestion-label {
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
}

.suggestion-chip {
    padding: 6px 12px;
    background-color: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    font-size: 12px;
    color: #4b5563;
    cursor: pointer;
    transition: all 0.2s;
}

.suggestion-chip:hover:not(:disabled) {
    background-color: #e5e7eb;
    border-color: #d1d5db;
}

.suggestion-chip:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Options Section */
.options-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background-color: #f9fafb;
    border-radius: 8px;
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #374151;
    cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
}

.checkbox-label input[type="checkbox"]:disabled {
    cursor: not-allowed;
}

/* Error Message */
.error-message {
    padding: 12px;
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    color: #dc2626;
    font-size: 14px;
}

/* Results Section */
.results-section {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.changes-summary {
    padding: 16px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 8px;
    color: white;
}

.changes-summary .section-title {
    color: white;
    margin-bottom: 8px;
}

.summary-text {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
}

.result-item {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
}

.result-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.section-title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
}

.apply-button {
    padding: 6px 16px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.2s;
}

.apply-button:hover:not(:disabled) {
    opacity: 0.9;
}

.apply-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.result-content {
    padding: 12px;
    background-color: #f9fafb;
    border-radius: 6px;
    font-size: 14px;
    line-height: 1.6;
}

/* Editable Fields */
.editable-title {
    width: 100%;
    padding: 12px;
    border: 2px solid #e5e7eb;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    font-family: inherit;
    background-color: white;
    transition: border-color 0.2s;
}

.editable-title:focus {
    outline: none;
    border-color: #667eea;
    background-color: #fefefe;
}

.editable-excerpt {
    width: 100%;
    padding: 12px;
    border: 2px solid #e5e7eb;
    border-radius: 6px;
    font-size: 14px;
    line-height: 1.6;
    color: #4b5563;
    font-family: inherit;
    background-color: white;
    resize: vertical;
    min-height: 80px;
    transition: border-color 0.2s;
}

.editable-excerpt:focus {
    outline: none;
    border-color: #667eea;
    background-color: #fefefe;
}

.editable-content {
    width: 100%;
    padding: 12px;
    border: 2px solid #e5e7eb;
    border-radius: 6px;
    font-size: 14px;
    line-height: 1.6;
    color: #4b5563;
    font-family: inherit;
    background-color: white;
    resize: vertical;
    min-height: 250px;
    transition: border-color 0.2s;
}

.editable-content:focus {
    outline: none;
    border-color: #667eea;
    background-color: #fefefe;
}

.title-preview {
    font-weight: 600;
    color: #1f2937;
}

.content-preview {
    color: #4b5563;
    white-space: pre-wrap;
}

.tags-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.tag-chip {
    padding: 4px 12px;
    background-color: #ddd6fe;
    color: #5b21b6;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
}

/* Metadata Section */
.metadata-section {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    padding: 16px;
    background-color: #f9fafb;
    border-radius: 8px;
}

.metadata-item {
    display: flex;
    gap: 6px;
    font-size: 13px;
}

.metadata-label {
    color: #6b7280;
    font-weight: 500;
}

.metadata-value {
    color: #1f2937;
    font-weight: 600;
}

.metadata-item.cached {
    color: #059669;
    font-weight: 600;
}

/* Action Buttons */
.action-buttons {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
}

.primary-button,
.secondary-button {
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
}

.primary-button {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    display: flex;
    align-items: center;
    gap: 8px;
}

.primary-button:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.primary-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

.secondary-button {
    background-color: white;
    border: 2px solid #e5e7eb;
    color: #374151;
}

.secondary-button:hover:not(:disabled) {
    background-color: #f9fafb;
    border-color: #d1d5db;
}

.secondary-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Loading Spinner */
.loading-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
